// ============ CORE SHELL & SHARED COMPONENTS ============
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ASSETS, INDICES, fmtNum, mmRng, HD_TYPES } from '../data';
import { MM_LIVE } from '../live';
import { mmIsPremium, mmLapsed } from '../lib/premium';
import { useLang, useT } from '../lib/i18n';

// LINE Official Account — add-friend link (configurable via .env)
const LINE_OA_URL = (import.meta.env.VITE_LINE_OA_URL as string) || 'https://lin.ee/shWOGYy3';
const LINE_OA_ID = (import.meta.env.VITE_LINE_OA_ID as string) || '@master-of-the-market';

// ---- Aurora / starfield background canvas ----
export function AuroraBG({ intensity }: { intensity: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current!;
    const ctx = cv.getContext('2d')!;
    let raf: number, W: number, H: number;
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      W = cv.clientWidth; H = cv.clientHeight;
      cv.width = W * DPR; cv.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    const rnd = mmRng(777);
    const stars = Array.from({ length: 90 }, () => ({
      x: rnd(), y: rnd(), r: 0.4 + rnd() * 1.3, p: rnd() * Math.PI * 2, s: 0.3 + rnd() * 0.8,
    }));
    const blobs = [
      { x: 0.18, y: 0.12, r: 0.55, hue: 'rgba(124,92,246,', sp: 0.00016, ph: 0 },
      { x: 0.85, y: 0.25, r: 0.45, hue: 'rgba(0,224,176,', sp: 0.00012, ph: 2 },
      { x: 0.55, y: 0.92, r: 0.6, hue: 'rgba(58,40,120,', sp: 0.0001, ph: 4 },
    ];
    let t = 0;
    const draw = () => {
      t += 16;
      ctx.clearRect(0, 0, W, H);
      const amp = intensity;
      blobs.forEach(b => {
        const bx = (b.x + Math.sin(t * b.sp + b.ph) * 0.06) * W;
        const by = (b.y + Math.cos(t * b.sp * 1.3 + b.ph) * 0.05) * H;
        const br = b.r * Math.max(W, H) * 0.55;
        const g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
        g.addColorStop(0, b.hue + 0.10 * amp + ')');
        g.addColorStop(1, b.hue + '0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
      ctx.fillStyle = '#fff';
      stars.forEach(s => {
        const tw = 0.25 + 0.75 * Math.abs(Math.sin(t * 0.001 * s.s + s.p));
        ctx.globalAlpha = tw * 0.5 * amp;
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mq.matches) draw(); else { t = 800; draw(); cancelAnimationFrame(raf!); }
    return () => { cancelAnimationFrame(raf!); window.removeEventListener('resize', resize); };
  }, [intensity]);
  return <canvas ref={ref} className="mm-aurora" aria-hidden="true"></canvas>;
}

// ---- Animated count-up number ----
export function CountUp({ value, decimals = 2, prefix = '', suffix = '', duration = 900 }: any) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current, to = value;
    prev.current = value;
    if (from === to) return;
    const t0 = performance.now();
    let raf: number;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setDisp(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{prefix}{fmtNum(disp, decimals)}{suffix}</span>;
}

// ---- Sparkline ----
export function Sparkline({ data, w = 110, h = 34, color, fill = true }: any) {
  const path = useMemo(() => {
    // Two guards, both for series this gets handed all the time. Empty: the
    // spread makes Math.min return Infinity. One point: i/(length-1) is 0/0,
    // which puts NaN in the path and voids the whole <path> - the browser drops
    // it without a word, so the sparkline just quietly stops appearing.
    if (!data?.length) return '';
    const min = Math.min(...data), max = Math.max(...data);
    const rng = max - min || 1;
    const span = data.length - 1;
    return data.map((v: number, i: number) =>
      `${i === 0 ? 'M' : 'L'}${(span ? (i / span) * w : w / 2).toFixed(1)},${(h - 3 - (v - min) / rng * (h - 6)).toFixed(1)}`
    ).join(' ');
  }, [data, w, h]);
  const up = data?.length ? data[data.length - 1] >= data[0] : true;
  const c = color || (up ? 'var(--up)' : 'var(--down)');
  const gid = useMemo(() => 'sg' + Math.random().toString(36).slice(2, 8), []);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mm-spark">
      {fill && (
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity="0.28"></stop>
            <stop offset="100%" stopColor={c} stopOpacity="0"></stop>
          </linearGradient>
        </defs>
      )}
      {fill && <path d={`${path} L${w},${h} L0,${h} Z`} fill={`url(#${gid})`} stroke="none"></path>}
      <path d={path} fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"></path>
    </svg>
  );
}

// ---- Radial gauge (score / RSI) ----
export function RadialGauge({ value, max = 100, size = 120, label, sub, color }: any) {
  const r = size / 2 - 9;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(1, value / max);
  const c = color || (pct > 0.66 ? 'var(--up)' : pct > 0.33 ? 'var(--gold)' : 'var(--down)');
  return (
    <div className="mm-gauge" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7"></circle>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth="7"
          strokeLinecap="round" strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.22,1,.36,1)', filter: `drop-shadow(0 0 6px ${c === 'var(--up)' ? 'rgba(0,224,176,.5)' : 'rgba(240,199,90,.4)'})` }}></circle>
      </svg>
      <div className="mm-gauge-txt">
        <strong style={{ color: c }}>{Math.round(value)}</strong>
        {label && <span>{label}</span>}
        {sub && <small>{sub}</small>}
      </div>
    </div>
  );
}

// ---- Donut chart ----
export function Donut({ items, size = 170, thickness = 22, centerTitle, centerSub }: any) {
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  const total = items.reduce((a: number, b: any) => a + b.v, 0);
  return (
    <div className="mm-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {items.map((it: any, i: number) => {
          const frac = it.v / total;
          const rot = acc / total * 360 - 90;
          acc += it.v;
          return (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={it.c} strokeWidth={thickness - 4}
              strokeDasharray={`${circ * frac - 3} ${circ - circ * frac + 3}`}
              transform={`rotate(${rot} ${size / 2} ${size / 2})`}
              className="mm-donut-seg" style={{ animationDelay: `${i * 90}ms` }}></circle>
          );
        })}
      </svg>
      <div className="mm-donut-c">
        <strong>{centerTitle}</strong>
        <span>{centerSub}</span>
      </div>
    </div>
  );
}

// ---- Ticker tape ----
export function TickerTape() {
  const last = MM_LIVE.last || {};
  const items = [...INDICES, ...ASSETS.slice(0, 10)].map(a => ({
    sym: a.sym, price: a.price, chg: a.chg,
  }));
  const row = items.map((it, i) => (
    <span className="mm-tape-item" key={i}>
      <b>{it.sym}</b>
      <span className={`mono ${last[it.sym] ? (last[it.sym] > 0 ? 'tick-up' : 'tick-dn') : ''}`} key={'p' + it.price}>{fmtNum(it.price, it.price > 1000 ? 0 : 2)}</span>
      <span className={`mono ${it.chg >= 0 ? 'up' : 'down'}`}>{it.chg >= 0 ? '▲' : '▼'} {Math.abs(it.chg).toFixed(2)}%</span>
    </span>
  ));
  return (
    <div className="mm-tape" aria-hidden="true">
      <div className="mm-tape-track">{row}{row}</div>
    </div>
  );
}

// ---- Nav definition ----
// `premium: true` puts the PRO badge on the nav entry. It marks any page with
// paid content behind a gate — whole pages (allocation, human design, track
// port) as well as pages that are free to browse but hold paid sections
// (dashboard's research cards, daily news' deep analysis and signals,
// analysis' options OI and earnings-gap study).
export const NAV = [
  { id: 'dashboard', th: 'ภาพรวมตลาด', en: 'Dashboard', icon: 'M3 13h4v8H3zM10 7h4v14h-4zM17 3h4v18h-4z', premium: true },
  { id: 'dailynews', th: 'วิเคราะห์ข่าวรายวัน', en: 'Daily News', icon: 'M4 4h13v16H4zM17 8h3v12h-3M7 8h7M7 12h7M7 16h4', premium: true },
  { id: 'analysis', th: 'วิเคราะห์รายตัว', en: 'Analysis', icon: 'M3 17l5-5 4 3 6-7 3 3M3 21h18', premium: true },
  { id: 'scanner', th: 'สแกนหุ้น', en: 'Scanner', icon: 'M11 11m-7 0a7 7 0 1014 0 7 7 0 10-14 0M21 21l-4.5-4.5' },
  { id: 'ipo', th: 'หุ้น IPO', en: 'IPO', icon: 'M12 3l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 10l6-1z' },
  { id: 'earnings', th: 'งบการเงิน', en: 'Earnings', icon: 'M4 4h16v16H4zM4 9h16M9 9v11' },
  { id: 'dividends', th: 'ปันผล', en: 'Dividends', icon: 'M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M12 7v5l3 3' },
  { id: 'allocation', th: 'จัดพอร์ต', en: 'Allocation', icon: 'M12 3a9 9 0 109 9h-9z M14 2a9 9 0 017 7h-7z', premium: true },
  { id: 'humandesign', th: 'ถอดรหัสนักลงทุน', en: 'Human Design', icon: 'M12 2l2.4 6.8L21 9l-5 4.6L17.5 21 12 17.2 6.5 21 8 13.6 3 9l6.6-.2z', premium: true },
  // soon: the page is behind MaintenanceGate. Flag it here too so the menu says
  // so before the click, instead of letting a reader arrive and find out.
  { id: 'courses', th: 'คอร์สเรียน', en: 'Academy', icon: 'M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 006.5 22H20V2H6.5A2.5 2.5 0 004 4.5z' },
  // 'soon' again: the page is back behind MaintenanceGate, so the menu has to
  // say so before the click rather than letting a reader arrive and find out.
  { id: 'trackport', th: 'ติดตามพอร์ต', en: 'Track Port', icon: 'M3 3v18h18M7 14l4-4 3 3 5-6', premium: true, soon: true },
  { id: 'legends', th: 'ตำนานนักลงทุน', en: 'Investor Legends', icon: 'M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8zM6 20h12M8 22h8' },
];

export function NavIcon({ d }: { d: string }) {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d={d}></path>
    </svg>
  );
}

// ---- Sidebar ----
export function Sidebar({ page, onNav, user }: any) {
  const { lang } = useLang();
  const t = useT();
  return (
    <aside className="mm-side">
      <div className="mm-logo" onClick={() => onNav('landing')} title={t('กลับสู่หน้าแรก', 'Back to home')}>
        <img className="mm-logo-img" src="/brand/logo-mark.png" alt="Master of the Market" />
        <div className="mm-logo-txt">
          <strong>MASTER</strong>
          <span><em>of the</em> MARKET</span>
        </div>
      </div>
      <nav className="mm-nav">
        {NAV.map(n => {
          // SOON wins over PRO: the feature is not finished, so inviting an
          // upgrade for it would be selling something we cannot deliver yet.
          // Admins see neither badge - the page opens for them.
          const soon = (n as any).soon && !user?.isAdmin;
          const locked = !soon && (n as any).premium && !mmIsPremium(user);
          return (
            <button key={n.id} className={`mm-nav-item ${page === n.id ? 'on' : ''}${soon ? ' soon' : ''}`} onClick={() => onNav(n.id)}>
              <NavIcon d={n.icon}></NavIcon>
              <span className="mm-nav-th">{lang === 'en' ? n.en : n.th}{soon && <i className="mm-soon">{lang === 'en' ? 'SOON' : 'เร็วๆ นี้'}</i>}{locked && <i className="mm-pro">PRO</i>}</span>
              <span className="mm-nav-en">{lang === 'en' ? n.th : n.en}</span>
            </button>
          );
        })}
      </nav>
      <div className="mm-side-foot">
        <a className="mm-line-btn" href={LINE_OA_URL} target="_blank" rel="noopener noreferrer">
          <span className="mm-line-ico">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 5.69 2 10.23c0 4.07 3.55 7.48 8.35 8.13.32.07.77.21.88.49.1.25.07.65.03.9l-.14.86c-.04.25-.2 1 .88.55 1.07-.46 5.77-3.4 7.87-5.82C21.27 14.04 22 12.23 22 10.23 22 5.69 17.52 2 12 2zM8.1 12.86H6.16c-.28 0-.51-.23-.51-.51V8.47c0-.28.23-.51.51-.51.28 0 .51.23.51.51v3.37H8.1c.28 0 .51.23.51.51s-.23.51-.51.51zm2.02-.51c0 .28-.23.51-.51.51s-.51-.23-.51-.51V8.47c0-.28.23-.51.51-.51s.51.23.51.51v3.88zm4.64 0c0 .22-.14.41-.35.48-.05.02-.11.03-.16.03-.16 0-.31-.08-.41-.21l-1.99-2.71v2.41c0 .28-.23.51-.51.51s-.51-.23-.51-.51V8.47c0-.22.14-.41.35-.48.05-.02.1-.03.16-.03.16 0 .31.08.41.21l1.99 2.71V8.47c0-.28.23-.51.51-.51s.51.23.51.51v3.88zm3.13-2.45c.28 0 .51.23.51.51s-.23.51-.51.51h-1.43v.92h1.43c.28 0 .51.23.51.51s-.23.51-.51.51h-1.94c-.28 0-.51-.23-.51-.51V8.47c0-.28.23-.51.51-.51h1.94c.28 0 .51.23.51.51s-.23.51-.51.51h-1.43v.92h1.43z"></path></svg>
          </span>
          <span className="mm-line-txt">
            <b>LINE Official</b>
            <em className="mono">{LINE_OA_ID}</em>
          </span>
        </a>
        <div className="mm-social-row">
          <a className="mm-social" href="https://discord.gg/NCYSpmZ2M" target="_blank" rel="noopener noreferrer" title="Discord" style={{ ['--sc' as any]: '#5865F2' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M20.32 4.37A19.8 19.8 0 0015.45 3l-.25.45c1.62.4 2.95 1.01 4.2 1.78a16.6 16.6 0 00-13.8 0c1.25-.77 2.58-1.38 4.2-1.78L9.55 3a19.8 19.8 0 00-4.87 1.37C1.6 8.95.76 13.42 1.18 17.83a19.9 19.9 0 006.1 3.07l.74-1.21c-.66-.25-1.29-.55-1.88-.91l.46-.34a14.2 14.2 0 0012.8 0l.46.34c-.59.36-1.22.66-1.88.91l.74 1.21a19.9 19.9 0 006.1-3.07c.5-5.1-.84-9.53-3.5-13.46zM8.5 15.1c-.95 0-1.73-.88-1.73-1.96 0-1.08.76-1.96 1.73-1.96.97 0 1.75.89 1.73 1.96 0 1.08-.77 1.96-1.73 1.96zm7 0c-.95 0-1.73-.88-1.73-1.96 0-1.08.76-1.96 1.73-1.96.97 0 1.75.89 1.73 1.96 0 1.08-.76 1.96-1.73 1.96z"></path></svg>
          </a>
          <a className="mm-social" href="https://youtu.be/b154Y9fsTXw" target="_blank" rel="noopener noreferrer" title="YouTube" style={{ ['--sc' as any]: '#FF0000' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M23 12s0-3.2-.4-4.74a2.5 2.5 0 00-1.76-1.77C19.3 5.3 12 5.3 12 5.3s-7.3 0-8.84.4a2.5 2.5 0 00-1.76 1.76C1 9 1 12 1 12s0 3.2.4 4.74a2.5 2.5 0 001.76 1.77c1.54.4 8.84.4 8.84.4s7.3 0 8.84-.4a2.5 2.5 0 001.76-1.77C23 15.2 23 12 23 12zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"></path></svg>
          </a>
          <a className="mm-social" href="https://www.tiktok.com/@samingpatrade" target="_blank" rel="noopener noreferrer" title="TikTok" style={{ ['--sc' as any]: '#69C9D0' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82a4.28 4.28 0 01-1.06-2.82h-3.3v13.18a2.59 2.59 0 01-2.59 2.46 2.59 2.59 0 110-5.18c.27 0 .53.04.78.12V9.96a5.88 5.88 0 00-.78-.05A5.92 5.92 0 004 15.83a5.92 5.92 0 0011.84 0V9.01a7.53 7.53 0 004.4 1.4V7.1a4.28 4.28 0 01-3.64-1.28z"></path></svg>
          </a>
          <a className="mm-social" href="https://www.instagram.com/samingpatrade" target="_blank" rel="noopener noreferrer" title="Instagram" style={{ ['--sc' as any]: '#E1306C' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5"></rect><circle cx="12" cy="12" r="4"></circle><circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"></circle></svg>
          </a>
          <a className="mm-social" href="https://www.facebook.com/SamingPatradeFanPage/" target="_blank" rel="noopener noreferrer" title="Facebook" style={{ ['--sc' as any]: '#1877F2' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z"></path></svg>
          </a>
        </div>
      </div>
    </aside>
  );
}

// ---- TopBar ----
export function TopBar({ onNav, user, onAuth, onLogout, onPick, title, topTools, onUpgrade }: any) {
  const { lang, setLang } = useLang();
  const t = useT();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const results = useMemo(() => {
    if (!q) return [];
    const s = q.toLowerCase();
    return ASSETS.filter(a => a.sym.toLowerCase().includes(s) || a.name.toLowerCase().includes(s)).slice(0, 6);
  }, [q]);
  return (
    <header className="mm-top">
      <div className="mm-top-title">
        <h1>{title}</h1>
        <span className="mm-live"><i></i>LIVE · {t(
          new Date().toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }),
          new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        )}</span>
      </div>
      <div className="mm-search">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"></circle><path d="M21 21l-4.5-4.5"></path></svg>
        <input placeholder={t('ค้นหาหุ้น, Crypto, ETF…  เช่น NVDA, PTT, BTC', 'Search stocks, crypto, ETF…  e.g. NVDA, PTT, BTC')} value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)} />
        {open && results.length > 0 && (
          <div className="mm-search-drop">
            {results.map(a => (
              <button key={a.sym} onMouseDown={() => { onPick(a.sym); setQ(''); }}>
                <b>{a.sym}</b><span>{a.name}</span>
                <em className={a.chg >= 0 ? 'up' : 'down'}>{a.chg >= 0 ? '+' : ''}{a.chg}%</em>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mm-top-actions">
        <button className="mm-lang" onClick={() => setLang(lang === 'th' ? 'en' : 'th')} title={t('เปลี่ยนภาษา', 'Switch language')}>
          <span className={lang === 'th' ? 'on' : ''}>TH</span>
          <span className={lang === 'en' ? 'on' : ''}>EN</span>
        </button>
        {topTools}
        {user && user.tier === 'Trial' && user.trialEnd && Date.now() < user.trialEnd && (
          // The countdown is the moment upgrading is on the reader's mind, so it
          // is the button rather than a label sitting next to one.
          <button className="mm-trial-chip mono" onClick={onUpgrade}
            title={t('สิทธิ์ทดลองใช้ Premium — กดเพื่ออัปเกรด', 'Premium trial — tap to upgrade')}>
            ⚡ <span className="mm-trial-long">{t('Trial เหลือ', 'Trial:')} </span>
            {Math.max(1, Math.ceil((user.trialEnd - Date.now()) / 864e5))}
            <span className="mm-trial-long"> {t('วัน', 'days')}</span>
          </button>
        )}
        {user ? (
          <React.Fragment>
            <div className="mm-user-chip">
              <Avatar user={user} className="mm-ava"></Avatar>
              <div>
                <b>{user.name}</b>
                <span>{user.hdType ? HD_TYPES[user.hdType].name : t('สมาชิก', 'Member')}</span>
              </div>
            </div>
            {/* Hidden on mobile (.mm-top-logout in styles.css): the header wraps
                there and this button claimed a whole row of its own above the
                content. Settings > Account still has a Log out button, so the
                way out survives on a phone - do not remove that one. */}
            <button className="mm-btn ghost mm-top-logout" onClick={onLogout}>{t('ออกจากระบบ', 'Log out')}</button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <button className="mm-btn ghost" onClick={() => onAuth('login')}>{t('เข้าสู่ระบบ', 'Log in')}</button>
            <button className="mm-btn primary" onClick={() => onAuth('signup')}>{t('สมัครสมาชิก', 'Sign up')}</button>
          </React.Fragment>
        )}
      </div>
    </header>
  );
}

// ---- Section heading ----
export function SecHead({ th, en, right }: any) {
  const { lang } = useLang();
  // `en` is already a proper English heading on every call site → swap by language.
  return (
    <div className="mm-sechead">
      <div>
        <h2>{lang === 'en' && en ? en : th}</h2>
        <span className="mm-sechead-en mono">{lang === 'en' ? th : en}</span>
      </div>
      {right && <div className="mm-sechead-r">{right}</div>}
    </div>
  );
}

// ---- Symbol logo ----------------------------------------------------------
// Company logo from the FMP CDN, falling back to the initial-letter badge that
// was here before. The CDN keys off the exchange-suffixed ticker (SPCG.BK, not
// SPCG) and does not carry every listing — a handful of small Thai names 404 —
// so the fallback is the normal case for some rows, not an error path.
const LOGO_CDN = 'https://images.financialmodelingprep.com/symbol/';

export function SymLogo({ sym, cdnSym, style, className = 'mm-scan-logo', fallback, optional }: {
  sym: string; cdnSym?: string | null; style?: React.CSSProperties; className?: string;
  fallback?: string; optional?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [cdnSym]); // reset when the row is reused for another symbol
  // `optional` is for places that already show the ticker next to the logo (the
  // IPO tables): with no logo there is nothing to add, so render nothing rather
  // than an empty plate. Most pre-listing companies have no logo on the CDN.
  if ((!cdnSym || broken) && optional) return null;
  if (!cdnSym || broken) return <span className={className} style={style}>{fallback ?? sym[0]}</span>;
  return (
    <img className={className} src={`${LOGO_CDN}${encodeURIComponent(cdnSym)}.png`} alt={sym} loading="lazy"
      // White plate: most brand marks are dark and would disappear on the dark table.
      style={{ background: '#fff', objectFit: 'contain', padding: 3, borderColor: 'rgba(255,255,255,.25)' }}
      onError={() => setBroken(true)} />
  );
}

// ---- Paid-members gate ----------------------------------------------------
// Same access rule as PremiumGate (a paid plan, or an unexpired 3-day trial),
// but it keeps the card visible behind a blur instead of replacing it: a
// research card nobody can see is a research card nobody subscribes for.
//
// This is presentation, not access control — the underlying endpoints stay
// public. Anything that genuinely must not leave the server needs gating there.
export function MemberGate({ user, onAuth, onUpgrade, title, desc, children }: any) {
  const t = useT();
  if (mmIsPremium(user)) return children;
  const lapsed = mmLapsed(user);
  return (
    <div className="mm-memgate">
      <div className="mm-memgate-veil" aria-hidden="true">{children}</div>
      <div className="mm-memgate-over">
        <div className="mm-gate-lock">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="11" width="16" height="10" rx="2.5"></rect><path d="M8 11V7a4 4 0 018 0v4"></path></svg>
        </div>
        <b>{title}</b>
        <p className="dim">{lapsed === 'paid'
          ? t(`แพ็กเกจ ${user.tier} ของคุณหมดอายุแล้ว — ต่ออายุเพื่อดูต่อ`, `Your ${user.tier} plan has expired — renew to keep reading`)
          : lapsed === 'trial'
            ? t('สิทธิ์ทดลอง Premium 3 วันหมดอายุแล้ว — ต่ออายุเพื่อดูต่อ', 'Your 3-day Premium trial has expired — renew to keep reading')
            : desc}</p>
        <div className="mm-memgate-actions">
          {user
            ? <button className="mm-btn primary" onClick={() => onUpgrade?.()}>{lapsed === 'paid'
                ? t('⚡ ต่ออายุ — Gold ฿359/เดือน · Premium ฿3,542/ปี', '⚡ Renew — Gold ฿359/mo · Premium ฿3,542/yr')
                : t('⚡ อัปเกรด — Gold ฿359/เดือน · Premium ฿3,542/ปี', '⚡ Upgrade — Gold ฿359/mo · Premium ฿3,542/yr')}</button>
            : <React.Fragment>
                <button className="mm-btn primary" onClick={() => onAuth?.('signup')}>{t('สมัคร — ทดลอง Premium ฟรี 3 วัน', 'Sign up — 3 days of Premium free')}</button>
                <button className="mm-btn ghost" onClick={() => onAuth?.('login')}>{t('เข้าสู่ระบบ', 'Log in')}</button>
              </React.Fragment>}
        </div>
        <span className="mm-gate-note mono dim">{lapsed === 'paid'
          ? t('แพ็กเกจหมดอายุ', 'PLAN EXPIRED')
          : lapsed === 'trial'
            ? t('สิทธิ์ทดลองหมดอายุ', 'TRIAL EXPIRED')
            : t('เฉพาะสมาชิกแบบเสียเงิน', 'PAID MEMBERS ONLY')}</span>
      </div>
    </div>
  );
}

// ---- Card-level error boundary ----
// The research cards render third-party JSON stored as received (nopoly,
// SuperTD, FMP disclosures). One field renamed upstream and the card threw -
// and the only boundary wrapped the whole page, so the whole Scanner or
// Earnings page went with it. This keeps a failure inside its own card.
export class CardBoundary extends React.Component<{ children: React.ReactNode; name?: string }, { err: boolean }> {
  state = { err: false };
  static getDerivedStateFromError() { return { err: true }; }
  componentDidCatch(e: unknown) { console.error(`[card] ${this.props.name || 'card'} failed to render`, e); }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="mm-card glass">
        <p className="dim" style={{ padding: '8px 2px', margin: 0 }}>ส่วนนี้แสดงผลไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง · This section failed to load — try refreshing</p>
      </div>
    );
  }
}

// ---- Member avatar ----
// A picture when there is one that loads, the initial otherwise. The plain
// <img> it replaces had no fallback, and Google's profile pictures were blocked
// by the site's own Content-Security-Policy until 1 Oct 2026 - every Google
// member without an uploaded photo had a broken image in the header. No
// referrer: Google's image host sometimes refuses a request that carries one.
export function Avatar({ user, className }: { user: any; className: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [user?.avatar]);
  const initial = String(user?.name || user?.email || 'M').trim().charAt(0).toUpperCase() || 'M';
  if (user?.avatar && !broken) {
    return <img className={className} src={user.avatar} alt={user.name || ''} referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return <div className={className}>{initial}</div>;
}

// ---- Verdict pill ----
export function Verdict({ v }: { v: 'buy' | 'sell' | 'neutral' }) {
  const map: Record<string, string[]> = { buy: ['ซื้อ', 'BUY'], sell: ['ขาย', 'SELL'], neutral: ['กลาง', 'HOLD'] };
  return <span className={`mm-verdict ${v}`}>{map[v][1]}</span>;
}
